/**
 * Servicio de Configuración Centralizada
 * Maneja toda la configuración de la aplicación de forma unificada
 */

import {
  AuthenticationMode,
  getAppConfig,
  getAuthConfig,
  isMultieventEnabled,
  getDefaultClient,
  getDefaultEventID
} from '../config/app.config';
import { Storage } from '@capacitor/storage';

export interface LoginRequestData {
  ced?: string;
  lookupCed?: string;
  idToken?: string;
  authMode?: AuthenticationMode;
  eventID?: string;
  multievent: boolean;
  client: string;
  action?: string; // Parámetro opcional para acciones específicas
}

export interface ServerAuthenticationConfig {
  mode: AuthenticationMode;
  requireEmailVerified: boolean;
  accountActivationEnabled: boolean;
  accountEndpoint?: string | null;
}

export interface AccountAccessRequest {
  email: string;
  eventID?: string;
}

export class AuthenticationApiError extends Error {
  constructor(public code: string, public status: number, message?: string) {
    super(message || code);
    this.name = 'AuthenticationApiError';
  }
}

// Interface removida - SmartLogin no implementado

export class ConfigService {
  private static instance: ConfigService;
  private readonly authenticationTimeoutMs = 12000;
  
  private constructor() {}
  
  public static getInstance(): ConfigService {
    if (!ConfigService.instance) {
      ConfigService.instance = new ConfigService();
    }
    return ConfigService.instance;
  }
  
  /**
   * Obtiene la configuración completa de la aplicación
   */
  public getConfig() {
    return getAppConfig();
  }
  
  /**
   * Obtiene la configuración de autenticación
   */
  public getAuthConfig() {
    return getAuthConfig();
  }

  /** Obtiene el método definido para este build de la aplicación. */
  public getAuthenticationMode(): AuthenticationMode {
    return this.getAuthConfig().mode || 'cedula';
  }

  public usesEmailPasswordAuthentication(): boolean {
    return this.getAuthenticationMode() === 'email_password';
  }

  /**
   * Verifica si multievent está habilitado
   */
  public isMultieventEnabled(): boolean {
    return isMultieventEnabled();
  }
  
  /**
   * Obtiene el cliente por defecto
   */
  public getDefaultClient(): string {
    return getDefaultClient();
  }

  /**
   * Obtiene el evento por defecto
   */
  public getDefaultEventID(): string | undefined {
    return getDefaultEventID();
  }
  
  // Smart login removido - no implementado
  
  /**
   * Construye los datos de request para login con múltiples eventos
   */
  public buildMultiEventLoginRequest(ced: string): LoginRequestData {
    const isMultievent = this.isMultieventEnabled();
    const request: LoginRequestData = {
      authMode: 'cedula',
      ced,
      multievent: isMultievent,
      client: this.getDefaultClient()
    };

    // Si multievent es false, agregar el evento por defecto
    if (!isMultievent) {
      const defaultEventID = this.getDefaultEventID();
      if (defaultEventID) {
        request.eventID = defaultEventID;
      }
    }

    return request;
  }
  
  /**
   * Construye los datos de request para login con evento específico
   */
  public buildSingleEventLoginRequest(ced: string, eventID: string): LoginRequestData {
    return {
      authMode: 'cedula',
      ced,
      eventID,
      multievent: false, // Siempre false para eventos específicos
      client: this.getDefaultClient()
    };
  }

  /**
   * Construye una consulta de participante para networking sin cambiar el
   * contrato legacy. En modo correo, `idToken` identifica a quien consulta y
   * `lookupCed` identifica al participante escaneado.
   */
  public buildNetworkingLookupRequest(
    ced: string,
    eventID: string,
    idToken?: string
  ): LoginRequestData {
    if (!this.usesEmailPasswordAuthentication()) {
      return {
        ...this.buildSingleEventLoginRequest(ced, eventID),
        action: 'get_user_info'
      };
    }

    if (!idToken) {
      throw new AuthenticationApiError('FIREBASE_SESSION_UNAVAILABLE', 401);
    }

    return {
      authMode: 'email_password',
      idToken,
      lookupCed: ced,
      eventID,
      multievent: false,
      client: this.getDefaultClient(),
      action: 'get_user_info'
    };
  }
  
  // Smart login removido - no implementado
  
  /**
   * Obtiene la URL canónica del endpoint de login.
   * No se usa el proxy local: el redirect del proxy puede descartar el body POST.
   */
  public getLoginEndpoint(): string {
    return `${this.getAuthApiBaseUrl()}/login.php`;
  }

  public getAuthConfigEndpoint(): string {
    return `${this.getAuthApiBaseUrl()}/auth_config.php`;
  }

  public getFirebaseAccountEndpoint(): string {
    return `${this.getAuthApiBaseUrl()}/firebase_account.php`;
  }

  private getAuthApiBaseUrl(): string {
    return (process.env.REACT_APP_AUTH_API_BASE_URL || 'https://esimposio.com/miesimposio/app_login')
      .replace(/\/$/, '');
  }
  
  /**
   * Obtiene las opciones de request configuradas para login
   */
  public getLoginRequestOptions(data: LoginRequestData): RequestInit {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    };
  }
  
  /**
   * Método de conveniencia para hacer login con múltiples eventos
   */
  public async loginMultiEvent(ced: string): Promise<Response> {
    const requestData = this.buildMultiEventLoginRequest(ced);
    const requestOptions = this.getLoginRequestOptions(requestData);
    const endpoint = this.getLoginEndpoint();
    
    console.log('🔄 ConfigService: Login multi-evento', {
      endpoint,
      multievent: requestData.multievent,
      client: requestData.client
    });
    
    return fetch(endpoint, requestOptions);
  }
  
  /**
   * Método de conveniencia para hacer login con evento específico
   */
  public async loginSingleEvent(ced: string, eventID: string): Promise<Response> {
    const requestData = this.buildSingleEventLoginRequest(ced, eventID);
    const requestOptions = this.getLoginRequestOptions(requestData);
    const endpoint = this.getLoginEndpoint();
    
    console.log('🔄 ConfigService: Login evento específico', {
      endpoint,
      eventID,
      multievent: requestData.multievent,
      client: requestData.client
    });
    
    return fetch(endpoint, requestOptions);
  }

  public buildFirebaseLoginRequest(idToken: string, eventID?: string): LoginRequestData {
    const resolvedEventID = eventID || this.getDefaultEventID();
    const isMultievent = this.isMultieventEnabled();

    if (!resolvedEventID && !isMultievent) {
      throw new AuthenticationApiError('EVENT_REQUIRED', 400);
    }

    const request: LoginRequestData = {
      idToken,
      authMode: 'email_password',
      multievent: !resolvedEventID && isMultievent,
      client: this.getDefaultClient()
    };

    if (resolvedEventID) request.eventID = resolvedEventID;
    return request;
  }

  public async loginWithFirebase(idToken: string, eventID?: string): Promise<Response> {
    return fetch(
      this.getLoginEndpoint(),
      this.getLoginRequestOptions(this.buildFirebaseLoginRequest(idToken, eventID))
    );
  }

  private async fetchAuthenticationJson(url: string, options: RequestInit = {}): Promise<any> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.authenticationTimeoutMs);

    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      let data: any = {};
      try {
        data = await response.json();
      } catch (_) {
        throw new AuthenticationApiError('INVALID_SERVER_RESPONSE', response.status || 502);
      }

      if (!response.ok || data.error) {
        throw new AuthenticationApiError(
          data.code || `HTTP_${response.status}`,
          response.status,
          data.message
        );
      }
      return data;
    } catch (error) {
      if (error instanceof AuthenticationApiError) throw error;
      if (error instanceof Error && error.name === 'AbortError') {
        throw new AuthenticationApiError('REQUEST_TIMEOUT', 408);
      }
      throw new AuthenticationApiError('NETWORK_ERROR', 0);
    } finally {
      window.clearTimeout(timeout);
    }
  }

  public async loadServerAuthenticationConfig(eventID?: string): Promise<ServerAuthenticationConfig> {
    const url = new URL(this.getAuthConfigEndpoint());
    url.searchParams.set('client', this.getDefaultClient());
    const resolvedEventID = eventID || this.getDefaultEventID();
    if (resolvedEventID) url.searchParams.set('eventID', resolvedEventID);

    const data = await this.fetchAuthenticationJson(url.toString(), { cache: 'no-store' });
    if (data.mode !== 'cedula' && data.mode !== 'email_password') {
      throw new AuthenticationApiError('INVALID_AUTH_MODE', 502);
    }

    const config: ServerAuthenticationConfig = {
      mode: data.mode,
      requireEmailVerified: Boolean(data.requireEmailVerified),
      accountActivationEnabled: Boolean(data.accountActivationEnabled),
      accountEndpoint: data.accountEndpoint || null
    };
    return config;
  }

  public async requestFirebaseAccess(request: AccountAccessRequest): Promise<{ requested: boolean; message: string }> {
    const eventID = request.eventID || this.getDefaultEventID();
    if (!eventID) throw new AuthenticationApiError('EVENT_REQUIRED', 400);

    const data = await this.fetchAuthenticationJson(this.getFirebaseAccountEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'request_access',
        authMode: 'email_password',
        email: request.email.trim().toLowerCase(),
        client: this.getDefaultClient(),
        eventID
      })
    });

    return {
      requested: Boolean(data.requested),
      message: data.message || 'Si el correo está registrado, recibirá instrucciones para activar o recuperar su cuenta.'
    };
  }
  
  // Smart login removido - no implementado
  
  /**
   * Obtiene información de configuración para debugging
   */
  public getConfigInfo() {
    const authConfig = this.getAuthConfig();

    return {
      authenticationMode: this.getAuthenticationMode(),
      multievent: authConfig.multievent,
      client: authConfig.client,
      defaultEventID: authConfig.defaultEventID,
      endpoint: this.getLoginEndpoint(),
      timestamp: new Date().toISOString()
    };
  }
  
  /**
   * Log de configuración para debugging
   */
  public logConfig() {
    console.log('📋 ConfigService - Configuración actual:', this.getConfigInfo());
  }

  // ==========================================
  // MÉTODOS PARA MANEJO DEL TUTORIAL
  // ==========================================

  /**
   * Verifica si el usuario ya ha visto el tutorial
   */
  public async hasSeenTutorial(): Promise<boolean> {
    try {
      const { value } = await Storage.get({ key: 'hasSeenTutorial' });
      return value === 'true';
    } catch (error) {
      console.error('Error al verificar tutorial:', error);
      return false;
    }
  }

  /**
   * Marca el tutorial como visto
   */
  public async setTutorialSeen(seen: boolean = true): Promise<void> {
    try {
      await Storage.set({ key: 'hasSeenTutorial', value: JSON.stringify(seen) });
      console.log('📚 Tutorial marcado como visto:', seen);
    } catch (error) {
      console.error('Error al marcar tutorial:', error);
    }
  }

  /**
   * Resetea el estado del tutorial (útil para testing)
   */
  public async resetTutorial(): Promise<void> {
    try {
      await Storage.remove({ key: 'hasSeenTutorial' });
      console.log('📚 Estado del tutorial reseteado');
    } catch (error) {
      console.error('Error al resetear tutorial:', error);
    }
  }

  /**
   * Obtiene información completa del estado del tutorial
   */
  public async getTutorialInfo(): Promise<{
    hasSeenTutorial: boolean;
    timestamp: string;
  }> {
    const hasSeenTutorial = await this.hasSeenTutorial();

    return {
      hasSeenTutorial,
      timestamp: new Date().toISOString()
    };
  }
}

// Exportar instancia singleton
export const configService = ConfigService.getInstance();

// Exportar funciones de conveniencia
export const buildMultiEventLoginRequest = (ced: string) => 
  configService.buildMultiEventLoginRequest(ced);

export const buildSingleEventLoginRequest = (ced: string, eventID: string) => 
  configService.buildSingleEventLoginRequest(ced, eventID);

export const getLoginEndpoint = () => 
  configService.getLoginEndpoint();

export const getLoginRequestOptions = (data: LoginRequestData) =>
  configService.getLoginRequestOptions(data);
